import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Send, Link2, Check, Copy, Loader2, MailCheck, Clock, Eye, CalendarCheck, ClipboardList } from "lucide-react";

export default function PreOutlookShare({ leadId, lead, defaultEmail }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [giorni, setGiorni] = useState(10);

  const a = lead?.analysis || {};
  const hasConsulenza = !!(a.pvgis || a.consumo_annuo_kwh || lead?.roof);

  useEffect(() => { setEmail(defaultEmail || ""); }, [defaultEmail]);
  const publicUrl = info?.token ? `${window.location.origin}/c/${info.token}` : "";

  const loadLink = async () => {
    try { const { data } = await api.get(`/leads/${leadId}/preoutlook-link`); setInfo(data); return data; }
    catch { return null; }
  };
  useEffect(() => { loadLink(); /* eslint-disable-next-line */ }, [leadId]);

  const copyLink = async () => {
    let d = info;
    if (!d?.token) d = await loadLink();
    const url = d?.token ? `${window.location.origin}/c/${d.token}` : "";
    if (!url) { toast.error("Link non disponibile"); return; }
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Link copiato"); }
    catch { toast.message(url); }
  };

  const send = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Inserisci un'email valida"); return; }
    const g = Math.max(1, Math.min(Number(giorni) || 10, 20));
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/send-preoutlook`, { email: email.trim(), origin_url: window.location.origin, giorni_validita: g });
      toast.success(`Consulenza inviata a ${data.sent_to}`);
      await loadLink(); setOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const appt = info?.appointment;

  return (
    <Card className="rounded border-border p-5" data-testid="preoutlook-share">
      <h2 className="font-heading font-semibold mb-1 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" strokeWidth={1.5} /> Consulenza (pre-Outlook)</h2>
      <p className="text-xs text-muted-foreground mb-4">Condividi l'analisi (senza preventivo) con link pubblico valido 5 giorni. Il cliente potrà prenotare un appuntamento con il consulente.</p>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setOpen(true)} className="font-semibold" disabled={!hasConsulenza} data-testid="send-preoutlook-btn">
          <Send className="h-4 w-4 mr-2" /> Invia consulenza via email
        </Button>
        <Button size="sm" variant="outline" onClick={copyLink} disabled={!hasConsulenza} data-testid="copy-preoutlook-link-btn">
          {copied ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Link2 className="h-4 w-4 mr-2" />} Copia link
        </Button>
      </div>
      {!hasConsulenza && <p className="text-xs text-warning mt-2">Completa prima l'analisi (bolletta/produzione/tetto).</p>}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
        {info?.sent_at && (
          <span className="inline-flex items-center gap-1.5 text-xs text-green-500 font-medium" data-testid="preoutlook-sent-status">
            <MailCheck className="h-3.5 w-3.5" /> Inviata a {info.sent_to} · {new Date(info.sent_at).toLocaleDateString("it-IT")}{info.sent_count > 1 ? ` (${info.sent_count}×)` : ""}
          </span>
        )}
        {info?.expires_at && (
          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Scade il {new Date(info.expires_at).toLocaleDateString("it-IT")}</span>
        )}
        {info?.opened_at && (
          <span className="inline-flex items-center gap-1.5 text-xs text-blue-500"><Eye className="h-3.5 w-3.5" /> Aperta · {new Date(info.opened_at).toLocaleDateString("it-IT")}</span>
        )}
      </div>

      {appt && (
        <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary/10 text-primary px-3 py-1.5 text-sm font-medium" data-testid="preoutlook-appointment">
          <CalendarCheck className="h-4 w-4" /> Appuntamento: {new Date(appt.start).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {appt.mode === "online" ? "Online" : "In visita"}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-popover" data-testid="send-preoutlook-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Invia la consulenza al cliente</DialogTitle>
            <DialogDescription>Il cliente riceverà un link riservato con l'analisi energetica e l'invito a prenotare un appuntamento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email destinatario</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.it" data-testid="preoutlook-email-input" className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Scadenza link (giorni)</label>
              <Input type="number" min={1} max={20} value={giorni} onChange={(e) => setGiorni(e.target.value)} data-testid="preoutlook-giorni-input" className="mt-1 max-w-[140px]" />
              <p className="text-[11px] text-muted-foreground mt-1">Consigliati 10 giorni · massimo 20.</p>
            </div>
            {publicUrl && (
              <div className="rounded-lg border border-border bg-secondary/30 p-2.5 flex items-center gap-2">
                <span className="text-xs text-muted-foreground truncate flex-1">{publicUrl}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={copyLink}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={send} disabled={sending} className="font-semibold" data-testid="confirm-send-preoutlook-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia consulenza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
